import { useEffect, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Globe } from "./Globe";

gsap.registerPlugin(ScrollTrigger);

const PHRASES = [
  { text: "MOVING GOODS.", sub: "01 — PHYSICAL FREIGHT", cls: "text-bone" },
  { text: "MOVING DATA.", sub: "02 — SOFTWARE & AUTOMATION", cls: "text-outline" },
  { text: "MOVING BUSINESS.", sub: "03 — A GLOBAL HOLDING", cls: "text-amber" },
];

const LABELS = [
  {
    kicker: "01 — Logistics",
    title: "Global physical movement",
    copy: "Ocean freight, land transportation, warehousing and port services.",
  },
  {
    kicker: "02 — Technology",
    title: "Routes become connections",
    copy: "Custom software, process automation and integration.",
  },
  {
    kicker: "03 — Digital Infrastructure",
    title: "Networks that move information",
    copy: "Digital transformation and technology consulting.",
  },
];

export default function HeroMaster() {
  const root = useRef(null);
  const progressRef = useRef(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      progressRef.current = 0.72;
      return undefined;
    }
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "+=1350%",
          scrub: 1,
          pin: true,
          anticipatePin: 1,
          onUpdate: (self) => {
            progressRef.current = self.progress;
          },
        },
      });

      /* Beat 1 — OA gains depth, geometry awakens (t 0–1.1) */
      tl.to(".scroll-hint", { opacity: 0, y: -10, duration: 0.25 }, 0);
      tl.to(".wm-oa", { scale: 1.12, duration: 1, ease: "power1.inOut" }, 0);
      tl.to(".oa-back", { yPercent: -7, opacity: 0.18, duration: 1, ease: "power1.inOut" }, 0);
      tl.to(".oa-back", { opacity: 0, duration: 0.35, ease: "power1.in" }, 1.05);
      tl.fromTo(".oa-ring", { scale: 0.85, opacity: 0 }, { scale: 1.15, opacity: 0.9, duration: 0.6 }, 0.3);
      tl.fromTo(".a-line-l", { scaleX: 0 }, { scaleX: 1, duration: 0.5, ease: "power2.out" }, 0.5);
      tl.fromTo(".a-line-r", { scaleX: 0 }, { scaleX: 1, duration: 0.5, ease: "power2.out" }, 0.62);

      /* Beat 2 — split, ring becomes horizon, OA HOLDING (t 1.1–2.6) */
      tl.to(".wm-o-wrap", { x: "-5vw", duration: 0.9, ease: "power2.inOut" }, 1.1);
      tl.to(".wm-a-wrap", { x: "5vw", duration: 0.9, ease: "power2.inOut" }, 1.1);
      tl.to(".wm-oa", { scale: 0.42, duration: 1, ease: "power2.inOut" }, 1.1);
      tl.to(".oa-ring", { scale: 26, opacity: 0.3, duration: 1.3, ease: "power2.in" }, 1.1);
      tl.to(".a-line-l, .a-line-r", { opacity: 0, duration: 0.3 }, 1.9);
      tl.fromTo(".wm-holding", { clipPath: "inset(0 100% 0 0)" }, { clipPath: "inset(0 0% 0 0)", duration: 0.8, ease: "power3.inOut" }, 1.55);
      tl.fromTo(".wm-holding-inner", { y: 0, yPercent: 60 }, { y: 0, yPercent: 0, duration: 0.8, ease: "power3.inOut" }, 1.55);
      tl.to(".oa-ring", { opacity: 0, duration: 0.4 }, 2.4);

      /* Beat 3 — wordmark exits, phrase cycle (t 2.6–6.3) */
      tl.to(".hero-stack", { y: "-32vh", scale: 0.55, opacity: 0, duration: 0.8, ease: "power2.inOut" }, 2.6);
      tl.set(".phrase", { opacity: 1 }, 2.85);
      tl.fromTo("#ph-0 .ph-in", { y: 0, yPercent: 130 }, { y: 0, yPercent: 0, duration: 0.45, ease: "power3.out" }, 2.9);
      tl.to("#ph-0 .ph-in", { yPercent: -130, duration: 0.4, ease: "power3.in" }, 3.75);
      tl.fromTo("#ph-1 .ph-in", { y: 0, yPercent: 130 }, { y: 0, yPercent: 0, duration: 0.45, ease: "power3.out" }, 3.95);
      tl.to("#ph-1 .ph-in", { yPercent: -130, duration: 0.4, ease: "power3.in" }, 4.8);
      tl.fromTo("#ph-2 .ph-in", { y: 0, yPercent: 130 }, { y: 0, yPercent: 0, duration: 0.45, ease: "power3.out" }, 5.0);
      /* MOVING BUSINESS scales away into the emerging particle field */
      tl.to("#ph-2 .ph-zoom", { scale: 6, opacity: 0, duration: 0.7, ease: "power2.in", transformOrigin: "50% 50%" }, 5.7);
      tl.fromTo(".wim-overline", { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.3 }, 5.2);

      /* Beat 4 — particles assemble, labels narrate (t 6.2–10.1, driven in 3D by progressRef) */
      tl.fromTo(".wim-label-1", { opacity: 0, y: 70 }, { opacity: 1, y: 0, duration: 0.45, ease: "power3.out" }, 6.3);
      tl.to(".wim-label-1", { opacity: 0, y: -70, duration: 0.4, ease: "power2.in" }, 7.35);
      tl.fromTo(".wim-label-2", { opacity: 0, y: 70 }, { opacity: 1, y: 0, duration: 0.45, ease: "power3.out" }, 7.75);
      tl.to(".wim-label-2", { opacity: 0, y: -70, duration: 0.4, ease: "power2.in" }, 8.7);
      tl.fromTo(".wim-label-3", { opacity: 0, y: 70 }, { opacity: 1, y: 0, duration: 0.45, ease: "power3.out" }, 9.1);
      tl.to(".wim-label-3", { opacity: 0, y: -70, duration: 0.4, ease: "power2.in" }, 9.95);

      /* Beat 5 — one holding (t 10.2–11.4) */
      tl.fromTo(".one-holding", { opacity: 0, scale: 0.94 }, { opacity: 1, scale: 1, duration: 0.5, ease: "power2.out" }, 10.2);
      tl.to(".one-holding", { opacity: 0, scale: 1.06, duration: 0.45, ease: "power2.in" }, 11.35);
      tl.to(".wim-overline", { opacity: 0, duration: 0.3 }, 11.3);

      /* Beat 6 — camera travels through the network, LOGISTICS handoff (t 10.9–13) */
      tl.fromTo(".handoff-line", { y: 0, yPercent: 120 }, { y: 0, yPercent: 0, duration: 0.6, ease: "power3.out" }, 11.9);
      tl.to({}, { duration: 0.55 });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={root}
      id="hero"
      className="relative h-[100svh] overflow-hidden"
      data-testid="hero-scene"
      aria-label="OA Holding — one continuous opening scene"
    >
      <div id="world" className="absolute inset-0" data-testid="world-globe">
        <Canvas
          camera={{ position: [0, 0.4, 3.8], fov: 42 }}
          dpr={[1, 1.5]}
          gl={{ antialias: true, alpha: true }}
          style={{ pointerEvents: "none" }}
        >
          <Globe progressRef={progressRef} />
        </Canvas>
      </div>
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse at center, transparent 45%, #050505 92%)" }}
        aria-hidden="true"
      />

      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute left-1/4 top-0 h-full w-px bg-white/5" />
        <div className="absolute left-2/4 top-0 h-full w-px bg-white/5" />
        <div className="absolute left-3/4 top-0 h-full w-px bg-white/5" />
      </div>

      <p className="wim-overline absolute left-[6vw] top-[14vh] z-20 font-mono text-[10px] uppercase tracking-[0.35em] text-smoke md:text-xs">
        02 — World in Motion
      </p>

      {/* OA wordmark */}
      <div className="absolute inset-0 z-10 flex items-center justify-center">
        <div className="hero-stack relative flex flex-col items-center gap-5 md:gap-7">
          <p className="hero-overline max-w-[88vw] text-center font-mono text-[10px] uppercase tracking-[0.35em] text-smoke md:text-xs">
            Global Shipping &amp; Software Solutions — Since 1999
          </p>
          <span
            className="oa-back pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none font-display text-[30vw] font-black leading-[0.8] tracking-tighter text-bone opacity-0 md:text-[23vw]"
            aria-hidden="true"
          >
            OA
          </span>
          <div className="relative flex flex-col items-center gap-2 md:flex-row md:items-baseline md:gap-[3vw]">
            <div className="wm-oa relative flex items-baseline">
              <span className="wm-o-wrap relative block">
                <span className="oa-ring pointer-events-none absolute left-1/2 top-[46%] block h-[0.74em] w-[0.74em] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/50" />
                <span className="block overflow-hidden">
                  <span className="wm-letter-inner block font-display text-[30vw] font-black leading-[0.8] tracking-tighter md:text-[23vw]">
                    O
                  </span>
                </span>
              </span>
              <span className="wm-a-wrap relative block">
                <span className="a-line-l absolute right-[82%] top-[62%] block h-[2px] w-[16vw] origin-right bg-white/40" />
                <span className="a-line-r absolute left-[82%] top-[62%] block h-[2px] w-[16vw] origin-left bg-amber/60" />
                <span className="block overflow-hidden">
                  <span className="wm-letter-inner block font-display text-[30vw] font-black leading-[0.8] tracking-tighter md:text-[23vw]">
                    A
                  </span>
                </span>
              </span>
            </div>
            <span className="wm-holding block overflow-hidden" data-testid="hero-wordmark-holding">
              <span className="wm-holding-inner block font-display text-[10vw] font-extrabold uppercase leading-[0.8] tracking-[0.06em] md:text-[8.5vw]">
                Holding
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* phrase cycle */}
      {PHRASES.map((p, i) => (
        <div
          key={p.text}
          id={`ph-${i}`}
          className="phrase pointer-events-none absolute inset-0 z-10"
          data-testid={`hero-phrase-${i}`}
          aria-hidden={i > 0}
        >
          <div className="ph-zoom flex h-full flex-col items-center justify-center">
            <div className="overflow-hidden px-4">
              <span className={`ph-in block text-center font-display text-[13vw] font-black uppercase leading-[0.9] tracking-tighter md:text-[10vw] ${p.cls}`}>
                {p.text}
              </span>
            </div>
            <div className="mt-5 overflow-hidden">
              <span className="ph-in block font-mono text-[10px] uppercase tracking-[0.35em] text-smoke md:text-xs">
                {p.sub}
              </span>
            </div>
          </div>
        </div>
      ))}

      {/* globe narration labels */}
      {LABELS.map((l, i) => (
        <div
          key={l.kicker}
          className={`wim-label wim-label-${i + 1} pointer-events-none absolute bottom-[12vh] left-[6vw] z-10 max-w-lg pr-6`}
          data-testid={`world-stage-${i + 1}`}
        >
          <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-amber md:text-xs">
            {l.kicker}
          </p>
          <h3 className="font-display text-[9vw] font-black uppercase leading-[0.9] tracking-tighter md:text-6xl">
            {l.title}
          </h3>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-smoke md:text-base">{l.copy}</p>
        </div>
      ))}

      {/* one holding */}
      <div
        className="one-holding pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center"
        data-testid="world-stage-final"
      >
        <p className="mb-6 font-mono text-[10px] uppercase tracking-[0.35em] text-smoke md:text-xs">
          Goods · Data · Business
        </p>
        <h3 className="font-display text-[8vw] font-black uppercase leading-[0.95] tracking-tighter md:text-8xl">
          One Holding.
        </h3>
        <h3 className="font-display text-[8vw] font-black uppercase leading-[0.95] tracking-tighter text-amber md:text-8xl">
          Multiple Infrastructures.
        </h3>
      </div>

      {/* logistics handoff */}
      <div className="pointer-events-none absolute bottom-[8vh] left-[6vw] z-10 overflow-hidden">
        <span
          className="handoff-line block font-display text-[18vw] font-black uppercase leading-[0.85] tracking-tighter md:text-[13vw]"
          data-testid="logistics-handoff"
        >
          Logistics
        </span>
      </div>

      <div
        className="scroll-hint absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
        data-testid="hero-scroll-hint"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-smoke">Scroll</span>
        <span className="scroll-line block h-10 w-px bg-amber" />
      </div>
    </section>
  );
}
